import { useEffect, useRef, useCallback } from 'react';
import type { PersistedQueueState, AudioTrack, RepeatMode } from '@/types/audio';

const STORAGE_KEY = 'spirit-of-shabbat-audio-queue';
const STALE_THRESHOLD_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const DEBOUNCE_MS = 1000; // 1 second debounce for saves

export interface QueueState {
  queue: AudioTrack[];
  currentIndex: number;
  currentTime: number;
  volume: number;
  playbackRate: number;
  shuffleMode: boolean;
  repeatMode: RepeatMode;
  activePlaylistId?: string;
  shuffledIndices?: number[];
}

export interface UseQueuePersistenceReturn {
  loadPersistedQueue: () => PersistedQueueState | null;
  saveQueue: (state: QueueState) => void;
  clearPersistedQueue: () => void;
}

/**
 * Hook for persisting audio queue state to localStorage
 * Provides debounced save to avoid excessive writes
 */
export function useQueuePersistence(): UseQueuePersistenceReturn {
  const saveTimeoutRef = useRef<number | null>(null);
  const pendingStateRef = useRef<QueueState | null>(null);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current !== null) {
        window.clearTimeout(saveTimeoutRef.current);
        // Flush pending save on unmount
        if (pendingStateRef.current) {
          saveToStorage(pendingStateRef.current);
        }
      }
    };
  }, []);

  /**
   * Load persisted queue state from localStorage
   * Returns null if no state exists or if state is stale
   */
  const loadPersistedQueue = useCallback((): PersistedQueueState | null => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return null;

      const state: PersistedQueueState = JSON.parse(stored);

      // Check if state is stale
      if (Date.now() - state.timestamp > STALE_THRESHOLD_MS) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }

      // Validate essential fields
      if (!Array.isArray(state.queue) || typeof state.currentIndex !== 'number') {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }

      return state;
    } catch (error) {
      console.error('Error loading persisted queue:', error);
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
  }, []);

  /**
   * Save queue state to localStorage (internal, not debounced)
   */
  const saveToStorage = (state: QueueState): void => {
    try {
      const persistedState: PersistedQueueState = {
        queue: state.queue,
        currentIndex: state.currentIndex,
        currentTime: state.currentTime,
        volume: state.volume,
        playbackRate: state.playbackRate,
        shuffleMode: state.shuffleMode,
        repeatMode: state.repeatMode,
        activePlaylistId: state.activePlaylistId,
        shuffledIndices: state.shuffledIndices,
        timestamp: Date.now(),
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(persistedState));
    } catch (error) {
      console.error('Error saving queue to localStorage:', error);
    }
  };

  /**
   * Save queue state with debouncing
   * Delays save by DEBOUNCE_MS to batch rapid updates
   */
  const saveQueue = useCallback((state: QueueState): void => {
    pendingStateRef.current = state;

    // Clear existing timeout
    if (saveTimeoutRef.current !== null) {
      window.clearTimeout(saveTimeoutRef.current);
    }

    // Set new timeout
    saveTimeoutRef.current = window.setTimeout(() => {
      if (pendingStateRef.current) {
        saveToStorage(pendingStateRef.current);
        pendingStateRef.current = null;
      }
      saveTimeoutRef.current = null;
    }, DEBOUNCE_MS);
  }, []);

  /**
   * Clear persisted queue from localStorage
   */
  const clearPersistedQueue = useCallback((): void => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      pendingStateRef.current = null;
      if (saveTimeoutRef.current !== null) {
        window.clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
    } catch (error) {
      console.error('Error clearing persisted queue:', error);
    }
  }, []);

  return {
    loadPersistedQueue,
    saveQueue,
    clearPersistedQueue,
  };
}

/**
 * Standalone function to load queue (for use outside React components)
 * @param storageKey - Optional custom storage key. If not provided, uses the default key.
 *                     Pass a user-scoped key to load state for a specific user.
 */
export function loadPersistedQueueState(storageKey?: string): PersistedQueueState | null {
  const key = storageKey || STORAGE_KEY;
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return null;

    const state: PersistedQueueState = JSON.parse(stored);

    // Check if state is stale
    if (Date.now() - state.timestamp > STALE_THRESHOLD_MS) {
      localStorage.removeItem(key);
      return null;
    }

    // Validate essential fields
    if (!Array.isArray(state.queue) || typeof state.currentIndex !== 'number') {
      localStorage.removeItem(key);
      return null;
    }

    return state;
  } catch {
    return null;
  }
}
